import { type FC, useCallback } from 'react'
import { formatScore, UiCollapsible, useDateFormat } from 'react-common'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import type { ObjectiveRecord } from '@/api/models/objectiveRecord'
import { useIsMobile } from '@/hooks/useIsMobile'
import { ArrowResultButton } from './ArrowResultButton'
import styles from './CourseResults.module.scss'

type CourseResultsProps = {
  objectiveRecords: ObjectiveRecord[]
  otherResultRecords: ObjectiveRecord[]
  className: string
  supportReview: boolean
  supportMobileView: boolean
  showPreviousAttemptScores: boolean
}

export const CourseResults: FC<CourseResultsProps> = ({
  objectiveRecords,
  className,
  otherResultRecords,
  supportReview,
  supportMobileView,
  showPreviousAttemptScores,
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { formatDateTime } = useDateFormat()

  const lastObjectiveRecord = objectiveRecords[0]

  const getBadge = useCallback((result: ObjectiveRecord) => {
    const label = formatScore(result)
    const grade = result.grade
    if (grade) {
      return `${grade}: ${label}`
    }

    return label
  }, [])

  const shouldShowReviewButton = (!isMobile || supportMobileView) && supportReview

  if (!lastObjectiveRecord) return null

  return (
    <section className={className}>
      {!!otherResultRecords.length && (
        <UiCollapsible title={<>{t('courseDetails.previousAttempts')}</>} initialState>
          <table className={styles.otherResultsTable}>
            <tbody>
              <tr hidden>
                <th scope="col">{t('courseDetails.previousAttemptsDate')}</th>
                {showPreviousAttemptScores && <th scope="col">{t('courseDetails.previousAttemptsScore')}</th>}
                {shouldShowReviewButton && <th scope="col">{t('courseDetails.previousAttemptsReviewAttempt')}</th>}
              </tr>

              {otherResultRecords.map((result) => (
                <tr key={result.id} className={styles.otherResultsRow}>
                  <td className={cn(styles.otherResultsCell, styles.colDate)}>{formatDateTime(result.finished)}</td>
                  {showPreviousAttemptScores && (
                    <td className={cn(styles.otherResultsCell, styles.colScore)}>
                      <span>{getBadge(result)}</span>
                    </td>
                  )}
                  {shouldShowReviewButton && <ArrowResultButton result={result} />}
                </tr>
              ))}
            </tbody>
          </table>
        </UiCollapsible>
      )}
    </section>
  )
}
